import { useState } from "react";
import { useGetOrdersQuery } from "../redux/apislice";
import { Player } from "@lottiefiles/react-lottie-player";

const statusClass = {
  PROCESSING: "bg-purple-100 text-purple-800",
  COMPLETED: "bg-green-100 text-green-800",
  REJECTED: "bg-red-100 text-red-800",
  "ON HOLD": "bg-amber-100 text-amber-800",
  PENDING: "bg-yellow-100 text-yellow-800",
};

export default function Orders() {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [buyerFilter, setBuyerFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10;

  const { data, error, isLoading } = useGetOrdersQuery({ 
    page: currentPage, 
    limit: ordersPerPage 
  });
  const closeModal = () => setSelectedOrder(null);

  const allOrders = data?.data || [];
  const totalPages = data?.totalPages || 0;
  const hasNext = data?.hasNext || false;
  const totalOrders = data?.total || 0;

  const filteredOrders = allOrders?.filter((order) => {
    const buyerMatch = order?.order?.user?.username?.toLowerCase()?.includes(buyerFilter?.toLowerCase());
    const statusMatch = statusFilter === "All" || order?.sellerPaymentStatus === statusFilter;
    return buyerMatch && statusMatch;
  });

  if (isLoading) {
   return (
         <div style={{
           display: 'flex',
           justifyContent: 'center',
           alignItems: 'center',
           height: '100vh',
           width: '100%',
         }}>
           <Player
             autoplay
             loop
             src="https://assets1.lottiefiles.com/packages/lf20_raiw2hpe.json"
             style={{ height: '150px', width: '150px' }}
           />
         </div>
       );
  }

  const hasNoProducts = allOrders?.length === 0;

  return (
    <div className="min-h-screen w-full bg-gray-50">
      <main className="container mx-auto px-4 py-8">
        {hasNoProducts ? (
          <div className=" rounded-lg shadow-sm p-8 text-center">
            <div className="max-w-md mx-auto">
              <svg
                className="mx-auto h-16 w-16 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
<h3 className="mt-4 text-lg font-medium text-gray-900">
  No orders yet Don't worry — every success story begins with an empty page!
</h3>
<p className="mt-2 text-sm text-gray-500">
  Stay consistent and keep going — your first sale is closer than you think. 
  Great businesses are built one step at a time, and you're already on the right path!
</p>
<div className="mt-6 text-left text-sm text-gray-600 space-y-2">
  <ul className="list-disc pl-5 space-y-1">
    <li>Every big seller once started with zero orders — persistence made them successful</li>
    <li>Your products have the potential to reach thousands of eager buyers</li>
    <li>Each day you stay active, you increase your chances of landing that first sale</li>
    <li>Growth may start small, but it builds momentum faster than you think</li>
  </ul>
  <p className="mt-2 text-gray-700 font-semibold">
    Keep moving forward — your first order could be the spark that ignites your entire journey!
  </p>
</div>              
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
              <h1 className="text-2xl font-bold text-gray-800 mb-4 md:mb-0">Order Management</h1>
              
              <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                <div className="relative flex-grow">
                  <input
                    type="text"
                    placeholder="Search by order Id."
                    value={buyerFilter}
                    onChange={(e) => {
                      setBuyerFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                  <svg
                    className="absolute right-3 top-3 h-4 w-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                >
                  <option value="All">All Statuses</option>
                  <option value="PENDING">Pending</option>
                  <option value="PROCESSING">Processing</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="ON HOLD">On Hold</option>
                </select>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">Failed to load orders. Please try again later.</p>
                  </div>
                </div>
              </div>
            )}

            {!isLoading && !error && (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Order ID
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Product
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Qty
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredOrders?.map((order) => (
                        <tr key={order?.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            #{order?.orderId}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {order?.product?.title?.split(" ")?.slice(0, 3)?.join(" ")}
                                  {order?.product?.title?.split(" ")?.length > 3 && "..."}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(order?.order?.createdAt)?.toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {order?.quantity}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            Rs {order?.total}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                statusClass[order?.sellerPaymentStatus] || "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {order?.sellerPaymentStatus}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => setSelectedOrder(order)}
                              className="text-blue-600 hover:text-blue-900 hover:underline transition-colors"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filteredOrders?.length === 0 && (
                  <div className="text-center py-12">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No orders found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Try adjusting your search or filter to find what you're looking for.
                    </p>
                  </div>
                )}
              </div>
            )}

            {!isLoading && totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * ordersPerPage + 1}</span> to{" "}
                    <span className="font-medium">
                      {Math.min(currentPage * ordersPerPage, totalOrders)}
                    </span>{" "}
                    of <span className="font-medium">{totalOrders}</span> results
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-4 py-2 border text-sm font-medium rounded-md ${
                          currentPage === pageNum
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages || !hasNext}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {selectedOrder && (
              <div className="fixed inset-0 mt-8 m-auto overflow-y-auto z-50">
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                  <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={closeModal}></div>
                  </div>
                  <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                  <div className="inline-block align-bottom  bg-white  rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                      <div className="sm:flex sm:items-start">
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                          <div className="flex justify-between items-start">
                            <h3 className="text-lg leading-6 font-medium text-gray-900">Order Details</h3>
                            <button
                              type="button"
                              className="text-gray-400 hover:text-gray-500 focus:outline-none"
                              onClick={closeModal}
                            >
                              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                          <div className="mt-4 grid grid-cols-1 gap-y-4 gap-x-8 sm:grid-cols-2">
                            <div>
                              <h4 className="text-sm font-medium text-gray-500">Order Information</h4>
                              <dl className="mt-2 space-y-2">
                                <div className="flex justify-between">
                                  <dt className="text-sm text-gray-500">Order ID</dt>
                                  <dd className="text-sm text-gray-900">#{selectedOrder?.orderId}</dd>
                                </div>
                                <div className="flex justify-between">
                                  <dt className="text-sm text-gray-500">Date</dt>
                                  <dd className="text-sm text-gray-900">
                                    {new Date(selectedOrder?.order?.createdAt)?.toLocaleString()}
                                  </dd>
                                </div>
                                <div className="flex justify-between">
                                  <dt className="text-sm text-gray-500">Status</dt>
                                  <dd className="text-sm">
                                    <span
                                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        statusClass[selectedOrder?.sellerPaymentStatus] || "bg-gray-100 text-gray-800"
                                      }`}
                                    >
                                      {selectedOrder?.sellerPaymentStatus}
                                    </span>
                                  </dd>
                                </div>
                              </dl>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-gray-500">Product Information</h4>
                              <dl className="mt-2 space-y-2">
                                <div className="flex justify-between">
                                  <dt className="text-sm text-gray-500">Title:</dt>
                                  <dd className="text-sm text-gray-900">{selectedOrder?.product?.title}</dd>
                                </div>
                                <div className="flex justify-between">
                                  <dt className="text-sm text-gray-500">Quantity</dt>
                                  <dd className="text-sm text-gray-900">{selectedOrder?.quantity}</dd>
                                </div>
                                {/* <div className="flex justify-between">
                                  <dt className="text-sm text-gray-500">Unit Price</dt>
                                  <dd className="text-sm text-gray-900">Rs {selectedOrder?.price}</dd>
                                </div> */}
                                <div className="flex justify-between">
                                  <dt className="text-sm text-gray-500">Base Price</dt>
                                  <dd className="text-sm text-gray-900">Rs {selectedOrder?.basePrice}</dd>
                                </div>
                                <div className="flex justify-between">
                                  <dt className="text-sm text-gray-500">Variant Price</dt>
                                  <dd className="text-sm text-gray-900">Rs {selectedOrder?.variantExtra}</dd>
                                </div>
                                 <div className="flex justify-between">
                                  <dt className="text-sm text-gray-500">Final Price</dt>
                                  <dd className="text-sm font-medium text-gray-900">Rs {selectedOrder?.finalPrice}</dd>
                                </div>
                                <div className="flex justify-between">
                                  <dt className="text-sm text-gray-500">Total Amount</dt>
                                  <dd className="text-sm font-medium text-gray-900">Rs {selectedOrder?.total}</dd>
                                </div>
                              </dl>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-gray-500">Customer Information</h4>
                              <dl className="mt-2 space-y-2">
                                <div className="flex justify-between">
                                  <dt className="text-sm text-gray-500">Username</dt>
                                  <dd className="text-sm text-gray-900">{selectedOrder?.order?.user?.username}</dd>
                                </div>
                                <div className="flex justify-between">
                                  <dt className="text-sm text-gray-500">Address</dt>
                                  <dd className="text-sm text-gray-900 text-right">
                                    {selectedOrder?.order?.street_address}, {selectedOrder?.order?.apartment}<br />
                                    {selectedOrder?.order?.city}
                                  </dd>
                                </div>
                              </dl>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-gray-500">Actions</h4>
                              <div className="mt-2 space-y-2">
                                <button className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                  </svg>
                                  Start Chat
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 px-4  sm:px-6 sm:flex sm:flex-row-reverse">
                      <button
                        type="button"
                        className=" mb-2 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-1 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                        onClick={closeModal}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}